import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StyledSelect } from '@/components/ui/styled-select';
import { 
  Building2, Users, TrendingUp, DollarSign, Plus, X, 
  Phone, Mail, Calendar, Clock, CheckCircle, AlertCircle,
  MessageSquare, Bell, BarChart3, Filter, Search, Edit, Trash2, Send,
  Crown, Trophy, Zap, Target, Star, Shield
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import LeadTimelineDrawer from '@/components/LeadTimelineDrawer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEAD_STATUSES = [
  { id: 'new', label: 'New', color: 'bg-blue-500' },
  { id: 'contacted', label: 'Contacted', color: 'bg-yellow-500' },
  { id: 'interested', label: 'Interested', color: 'bg-purple-500' },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-orange-500' },
  { id: 'closed_won', label: 'Closed Won', color: 'bg-emerald-500' },
  { id: 'closed_lost', label: 'Closed Lost', color: 'bg-red-500' }
];

const TASK_PRIORITIES = [
  { id: 'low', label: 'Low', color: 'text-gray-400' },
  { id: 'medium', label: 'Medium', color: 'text-yellow-400' },
  { id: 'high', label: 'High', color: 'text-red-400' }
];

// Email templates
const EMAIL_TEMPLATES = [
  { id: 'introduction', label: 'Introduction', subject: 'Welcome to ASTIIV BASGAT - Your Luxury Journey Begins' },
  { id: 'property_showcase', label: 'Property Showcase', subject: 'Exclusive Properties Matching Your Preferences' },
  { id: 'follow_up', label: 'Follow Up', subject: 'Following Up on Our Conversation' },
  { id: 'meeting_request', label: 'Meeting Request', subject: 'Schedule a Viewing at Your Convenience' },
  { id: 'custom', label: 'Custom Email', subject: '' }
];

// v1.1 Lead Intelligence Tiers
const LEAD_TIERS = {
  Platinum: { 
    color: 'bg-gradient-to-r from-slate-400 to-slate-600', 
    textColor: 'text-white',
    icon: Crown,
    label: 'Platinum Lead',
    description: 'High-intent, ready to buy'
  },
  Gold: { 
    color: 'bg-gradient-to-r from-yellow-500 to-amber-600', 
    textColor: 'text-black',
    icon: Trophy,
    label: 'Gold Lead',
    description: 'Warm lead, active interest'
  },
  Silver: { 
    color: 'bg-gradient-to-r from-gray-400 to-gray-500', 
    textColor: 'text-white',
    icon: Star,
    label: 'Silver Lead',
    description: 'Moderate interest'
  },
  Nurture: { 
    color: 'bg-gray-600', 
    textColor: 'text-white',
    icon: Target,
    label: 'Nurture',
    description: 'Needs nurturing'
  }
};

// Helper to get lead tier display
const getLeadTierBadge = (tier) => {
  const tierInfo = LEAD_TIERS[tier] || LEAD_TIERS.Nurture;
  const Icon = tierInfo.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${tierInfo.color} ${tierInfo.textColor}`}>
      <Icon className="w-3 h-3" />
      {tier || 'Nurture'}
    </span>
  );
};

// Helper to get intent level color
const getIntentColor = (intent) => {
  switch (intent) {
    case 'High': return 'text-emerald-400';
    case 'Medium': return 'text-yellow-400';
    case 'Low': return 'text-gray-400';
    default: return 'text-gray-400';
  }
};

const PartnerCRM = ({ partnerId }) => {
  const [activeTab, setActiveTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [timelineLeadId, setTimelineLeadId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [sendingEmail, setSendingEmail] = useState(false);

  const [newLead, setNewLead] = useState({
    name: '', email: '', phone: '', source: 'website', interest: '', status: 'new', estimated_value: ''
  });

  const [newTask, setNewTask] = useState({
    title: '', description: '', due_date: '', priority: 'medium', lead_id: ''
  });

  const [emailForm, setEmailForm] = useState({
    template: 'introduction',
    subject: 'Welcome to ASTIIV BASGAT - Your Luxury Journey Begins',
    body: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      
      const headers = { Authorization: `Bearer ${token}` };

      const [leadsRes, tasksRes, remindersRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/partner/crm/leads`, { headers }),
        axios.get(`${API}/partner/crm/tasks`, { headers }),
        axios.get(`${API}/partner/crm/reminders`, { headers }),
        axios.get(`${API}/partner/crm/analytics`, { headers })
      ]);

      setLeads(leadsRes.data);
      setTasks(tasksRes.data);
      setReminders(remindersRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLead = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/partner/crm/leads`, {
        ...newLead,
        partner_id: partnerId,
        estimated_value: newLead.estimated_value ? parseFloat(newLead.estimated_value) : null
      }, { headers });

      toast.success('Lead created!');
      setShowAddLeadModal(false);
      setNewLead({ name: '', email: '', phone: '', source: 'website', interest: '', status: 'new', estimated_value: '' });
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create lead');
    }
  };

  const handleUpdateLeadStatus = async (leadId, newStatus) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/partner/crm/leads/${leadId}`, { status: newStatus, stage: newStatus }, { headers });
      toast.success('Lead updated!');
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update lead');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!confirm('Are you sure you want to delete this lead?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/partner/crm/leads/${leadId}`, { headers });
      toast.success('Lead deleted!');
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete lead');
    }
  };

  const handleCreateTask = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/partner/crm/tasks`, {
        ...newTask,
        partner_id: partnerId
      }, { headers });

      toast.success('Task created!');
      setShowAddTaskModal(false);
      setNewTask({ title: '', description: '', due_date: '', priority: 'medium', lead_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/partner/crm/tasks/${taskId}`, { status: newStatus }, { headers });
      toast.success('Task updated!');
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update task');
    }
  };

  const handleOpenEmailModal = (lead) => {
    // PII-safe — we never have the customer's real email on the partner side.
    // The backend's /partner/crm/send-email uses the lead_id to look up the
    // recipient on the server (admin-owned customer data) so partners can
    // reach out without ever holding the email locally.
    const displayName = lead.customer_first_name || (lead.name ? lead.name.split(' ')[0] : 'Member');
    setSelectedLead({ ...lead, _displayName: displayName });
    setEmailForm({
      template: 'introduction',
      subject: 'Welcome to ASTIIV BASGAT - Your Luxury Journey Begins',
      body: `Dear ${displayName},\n\nThank you for your interest in ASTIIV BASGAT's exclusive luxury offerings.\n\nI noticed you're interested in ${lead.interest || lead.property_title || 'our luxury properties'}. I'd love to discuss how we can help you find the perfect match.\n\nWould you be available for a brief call this week?\n\nBest regards`
    });
    setShowEmailModal(true);
  };

  const handleTemplateChange = (templateId) => {
    const template = EMAIL_TEMPLATES.find(t => t.id === templateId);
    let body = '';
    
    switch(templateId) {
      case 'introduction':
        body = `Dear ${selectedLead?.name},\n\nThank you for your interest in ASTIIV BASGAT's exclusive luxury offerings.\n\nI noticed you're interested in ${selectedLead?.interest || 'luxury properties'}. I'd love to discuss how we can help you find the perfect match.\n\nWould you be available for a brief call this week?\n\nBest regards`;
        break;
      case 'property_showcase':
        body = `Dear ${selectedLead?.name},\n\nI'm excited to share some exclusive properties that match your preferences.\n\nBased on your interest in ${selectedLead?.interest || 'luxury real estate'}, I've curated a selection of premium options that I believe you'll find exceptional.\n\nI'd love to schedule a private viewing at your convenience.\n\nBest regards`;
        break;
      case 'follow_up':
        body = `Dear ${selectedLead?.name},\n\nI wanted to follow up on our recent conversation about ${selectedLead?.interest || 'your luxury requirements'}.\n\nHave you had a chance to consider the options we discussed? I'm here to answer any questions you may have.\n\nLooking forward to hearing from you.\n\nBest regards`;
        break;
      case 'meeting_request':
        body = `Dear ${selectedLead?.name},\n\nI would like to invite you for an exclusive viewing of some remarkable properties that match your criteria.\n\nPlease let me know your availability, and I'll arrange a private tour at your convenience.\n\nBest regards`;
        break;
      default:
        body = '';
    }
    
    setEmailForm({
      template: templateId,
      subject: template?.subject || '',
      body
    });
  };

  const handleSendEmail = async () => {
    if (!selectedLead || !emailForm.subject || !emailForm.body) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSendingEmail(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/partner/crm/send-email`, {
        lead_id: selectedLead.id,
        to_email: selectedLead.email,
        to_name: selectedLead.name,
        subject: emailForm.subject,
        body: emailForm.body
      }, { headers });
      
      toast.success(`Email sent to ${selectedLead.name}!`);
      setShowEmailModal(false);
      setSelectedLead(null);
      
      // Update lead status to contacted if it was new
      if (selectedLead.status === 'new') {
        await handleUpdateLeadStatus(selectedLead.id, 'contacted');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 0 }).format(amount);
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    const matchesTier = tierFilter === 'all' || lead.lead_tier === tierFilter;
    return matchesSearch && matchesStatus && matchesTier;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gold">Loading CRM...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Analytics Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass-card p-4">
          <Users className="w-6 h-6 text-gold mb-2" />
          <div className="text-2xl font-bold">{analytics?.leads?.total || 0}</div>
          <div className="text-text-secondary text-sm">Total Leads</div>
        </div>
        <div className="glass-card p-4">
          <TrendingUp className="w-6 h-6 text-emerald-400 mb-2" />
          <div className="text-2xl font-bold">{analytics?.conversion_rate || 0}%</div>
          <div className="text-text-secondary text-sm">Conversion Rate</div>
        </div>
        <div className="glass-card p-4">
          <DollarSign className="w-6 h-6 text-gold mb-2" />
          <div className="text-2xl font-bold">{formatCurrency(analytics?.total_revenue)}</div>
          <div className="text-text-secondary text-sm">Total Revenue</div>
        </div>
        <div className="glass-card p-4">
          <Clock className="w-6 h-6 text-orange-400 mb-2" />
          <div className="text-2xl font-bold">{analytics?.tasks?.pending || 0}</div>
          <div className="text-text-secondary text-sm">Pending Tasks</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        {['leads', 'tasks', 'pipeline'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-t-lg transition-colors capitalize ${
              activeTab === tab ? 'bg-gold text-black' : 'text-text-secondary hover:bg-white/5'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Leads Tab */}
      {activeTab === 'leads' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 flex-1 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary w-4 h-4" />
                <Input
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white text-black"
                />
              </div>
              <StyledSelect
                value={statusFilter}
                onChange={(v) => setStatusFilter(v)}
                options={[{ value: 'all', label: 'All Status' }, ...LEAD_STATUSES.map((s) => ({ value: s.id, label: s.label }))]}
                className="min-w-[140px]"
                data-testid="lead-status-filter"
              />
              <StyledSelect
                value={tierFilter}
                onChange={(v) => setTierFilter(v)}
                options={[
                  { value: 'all', label: 'All Tiers' },
                  { value: 'Platinum', label: 'Platinum Leads' },
                  { value: 'Gold', label: 'Gold Leads' },
                  { value: 'Silver', label: 'Silver Leads' },
                  { value: 'Nurture', label: 'Nurture Leads' },
                ]}
                className="min-w-[140px]"
                data-testid="tier-filter"
              />
            </div>
            <Button onClick={() => setShowAddLeadModal(true)} className="bg-gold text-black hover:bg-gold-light">
              <Plus className="w-4 h-4 mr-2" /> Add Lead
            </Button>
          </div>

          <div className="glass-card overflow-x-auto -mx-4 sm:mx-0 sm:rounded-2xl">
            <table className="w-full min-w-[760px]">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left p-4 text-text-secondary font-medium">Lead</th>
                  <th className="text-left p-4 text-text-secondary font-medium">Tier</th>
                  <th className="text-left p-4 text-text-secondary font-medium">Interest</th>
                  <th className="text-left p-4 text-text-secondary font-medium">Meeting</th>
                  <th className="text-left p-4 text-text-secondary font-medium">Status</th>
                  <th className="text-left p-4 text-text-secondary font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => {
                  // PII-safe display name — prefer the redacted customer_first_name
                  // (returned by /api/partner/crm/leads after iter 58 PII strip),
                  // fall back to whatever `name` is set on legacy manual entries.
                  const displayName = lead.customer_first_name
                    || (lead.name ? lead.name.split(' ')[0] : 'Member');
                  const meetingDate = lead.preferred_date || lead.meeting_date;
                  const meetingTime = lead.preferred_time || lead.meeting_time;
                  return (
                  <tr key={lead.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`lead-row-${lead.id}`}>
                    <td className="p-4">
                      <div className="font-semibold" data-testid={`lead-name-${lead.id}`}>{displayName}</div>
                      <div className="text-text-secondary text-xs mt-0.5">ID: {lead.dl_lead_id || lead.id?.slice(0, 8)}</div>
                      {lead.summary && (
                        <div className="text-text-secondary text-xs mt-1 italic">{lead.summary}</div>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="space-y-1">
                        {getLeadTierBadge(lead.lead_tier)}
                        {lead.lead_score && (
                          <div className="text-xs text-text-secondary">
                            Score: <span className="text-gold font-semibold">{lead.lead_score}</span>
                          </div>
                        )}
                        {lead.estimated_intent && (
                          <div className={`text-xs ${getIntentColor(lead.estimated_intent)}`}>
                            Intent: {lead.estimated_intent}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="text-text-secondary">{lead.interest || lead.property_title || '—'}</div>
                      {lead.recommended_action && (
                        <div className="text-xs mt-1 text-gold">
                          {lead.recommended_action}
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      {meetingDate ? (
                        <>
                          <div className="text-text-primary text-sm font-medium" data-testid={`lead-meeting-date-${lead.id}`}>
                            {new Date(meetingDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </div>
                          {meetingTime && (
                            <div className="text-text-secondary text-xs" data-testid={`lead-meeting-time-${lead.id}`}>{meetingTime}</div>
                          )}
                        </>
                      ) : (
                        <span className="text-text-secondary text-sm">—</span>
                      )}
                    </td>
                    <td className="p-4">
                      <StyledSelect
                        value={lead.status}
                        onChange={(v) => handleUpdateLeadStatus(lead.id, v)}
                        options={LEAD_STATUSES.map((s) => ({ value: s.id, label: s.label }))}
                        className="text-sm h-8"
                        data-testid={`lead-status-select-${lead.id}`}
                      />
                      {lead.concierge_priority && (
                        <div className="mt-1">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gold/20 text-gold">
                            <Zap className="w-3 h-3" /> Concierge Priority
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenEmailModal(lead)} className="text-text-secondary hover:text-gold" title="Send Email">
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setTimelineLeadId(lead.id)} className="text-text-secondary hover:text-gold" title="Privacy timeline" data-testid={`open-timeline-btn-${lead.id}`}>
                          <Shield className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteLead(lead.id)} className="text-text-secondary hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {filteredLeads.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-text-secondary">
                      No leads found. Add your first lead to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowAddTaskModal(true)} className="bg-gold text-black hover:bg-gold-light">
              <Plus className="w-4 h-4 mr-2" /> Add Task
            </Button>
          </div>

          <div className="grid gap-4">
            {tasks.map((task) => (
              <div key={task.id} className="glass-card p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <input
                    type="checkbox"
                    checked={task.status === 'completed'}
                    onChange={() => handleUpdateTaskStatus(task.id, task.status === 'completed' ? 'pending' : 'completed')}
                    className="w-5 h-5 rounded"
                  />
                  <div>
                    <div className={`font-semibold ${task.status === 'completed' ? 'line-through text-text-secondary' : ''}`}>
                      {task.title}
                    </div>
                    {task.description && <div className="text-text-secondary text-sm">{task.description}</div>}
                    <div className="flex items-center gap-4 mt-1 text-xs">
                      <span className="flex items-center gap-1 text-text-secondary">
                        <Calendar className="w-3 h-3" /> {task.due_date}
                      </span>
                      <span className={TASK_PRIORITIES.find(p => p.id === task.priority)?.color}>
                        {task.priority} priority
                      </span>
                    </div>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${
                  task.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                  task.status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-white/10 text-text-secondary'
                }`}>
                  {task.status}
                </span>
              </div>
            ))}
            {tasks.length === 0 && (
              <div className="glass-card p-8 text-center text-text-secondary">
                No tasks yet. Create your first task.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pipeline Tab */}
      {activeTab === 'pipeline' && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {LEAD_STATUSES.map((status) => {
            const statusLeads = leads.filter(l => l.status === status.id);
            return (
              <div key={status.id} className="space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-3 h-3 rounded-full ${status.color}`} />
                  <span className="font-semibold text-sm">{status.label}</span>
                  <span className="text-text-secondary text-xs">({statusLeads.length})</span>
                </div>
                <div className="space-y-2 min-h-[200px]">
                  {statusLeads.map((lead) => (
                    <div key={lead.id} className="glass-card p-3 text-sm cursor-pointer hover:border-gold/30 transition-colors">
                      <div className="font-semibold truncate">{lead.name}</div>
                      <div className="text-text-secondary text-xs truncate">{lead.interest}</div>
                      {lead.estimated_value && (
                        <div className="text-gold text-xs mt-1">{formatCurrency(lead.estimated_value)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Lead Modal */}
      {showAddLeadModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="glass-card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-playfair text-xl font-semibold">Add New Lead</h3>
              <Button variant="ghost" onClick={() => setShowAddLeadModal(false)}><X className="w-5 h-5" /></Button>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input value={newLead.name} onChange={(e) => setNewLead({...newLead, name: e.target.value})} className="bg-white text-black" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={newLead.email} onChange={(e) => setNewLead({...newLead, email: e.target.value})} className="bg-white text-black" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={newLead.phone} onChange={(e) => setNewLead({...newLead, phone: e.target.value})} className="bg-white text-black" />
              </div>
              <div>
                <Label>Interest</Label>
                <Input value={newLead.interest} onChange={(e) => setNewLead({...newLead, interest: e.target.value})} className="bg-white text-black" placeholder="e.g., Dubai Marina Apartment" />
              </div>
              <div>
                <Label>Estimated Value (AED)</Label>
                <Input type="number" value={newLead.estimated_value} onChange={(e) => setNewLead({...newLead, estimated_value: e.target.value})} className="bg-white text-black" />
              </div>
              <div>
                <Label>Source</Label>
                <StyledSelect
                  value={newLead.source}
                  onChange={(v) => setNewLead({...newLead, source: v})}
                  options={[
                    { value: 'website', label: 'Website' },
                    { value: 'referral', label: 'Referral' },
                    { value: 'social_media', label: 'Social Media' },
                    { value: 'walk_in', label: 'Walk-in' },
                    { value: 'other', label: 'Other' },
                  ]}
                  data-testid="new-lead-source-select"
                />
              </div>
              <Button onClick={handleCreateLead} disabled={!newLead.name || !newLead.email} className="w-full bg-gold text-black hover:bg-gold-light">
                Create Lead
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Task Modal */}
      {showAddTaskModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="glass-card p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-playfair text-xl font-semibold">Add New Task</h3>
              <Button variant="ghost" onClick={() => setShowAddTaskModal(false)}><X className="w-5 h-5" /></Button>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input value={newTask.title} onChange={(e) => setNewTask({...newTask, title: e.target.value})} className="bg-white text-black" />
              </div>
              <div>
                <Label>Description</Label>
                <textarea value={newTask.description} onChange={(e) => setNewTask({...newTask, description: e.target.value})} className="w-full bg-white text-black rounded p-2 min-h-[80px]" />
              </div>
              <div>
                <Label>Due Date *</Label>
                <Input type="date" value={newTask.due_date} onChange={(e) => setNewTask({...newTask, due_date: e.target.value})} className="bg-white text-black" />
              </div>
              <div>
                <Label>Priority</Label>
                <StyledSelect
                  value={newTask.priority}
                  onChange={(v) => setNewTask({...newTask, priority: v})}
                  options={TASK_PRIORITIES.map((p) => ({ value: p.id, label: p.label }))}
                  data-testid="new-task-priority-select"
                />
              </div>
              <div>
                <Label>Related Lead</Label>
                <StyledSelect
                  value={newTask.lead_id || ''}
                  onChange={(v) => setNewTask({...newTask, lead_id: v})}
                  placeholder="None"
                  options={leads.map((l) => ({ value: l.id, label: l.name }))}
                  data-testid="new-task-lead-select"
                />
              </div>
              <Button onClick={handleCreateTask} disabled={!newTask.title || !newTask.due_date} className="w-full bg-gold text-black hover:bg-gold-light">
                Create Task
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Send Email Modal */}
      {showEmailModal && selectedLead && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-lenis-prevent>
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="glass-card p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto overscroll-contain"
            style={{ scrollbarGutter: 'stable' }}
            data-lenis-prevent
          >
            <div className="flex justify-between items-center mb-6 sticky top-0 bg-obsidian/90 backdrop-blur-sm pb-4 -mt-2 pt-2 z-10">
              <div>
                <h3 className="font-playfair text-xl font-semibold flex items-center gap-2">
                  <Mail className="w-5 h-5 text-gold" />
                  Send Email
                </h3>
                <p className="text-text-secondary text-sm mt-1">To: {selectedLead.name} ({selectedLead.email})</p>
              </div>
              <Button variant="ghost" onClick={() => { setShowEmailModal(false); setSelectedLead(null); }}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="space-y-4">
              {/* Template Selection */}
              <div>
                <Label className="text-text-secondary mb-2 block">Email Template</Label>
                <div className="flex flex-wrap gap-2">
                  {EMAIL_TEMPLATES.map(template => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateChange(template.id)}
                      className={`px-3 py-2 rounded text-sm transition-colors ${
                        emailForm.template === template.id
                          ? 'bg-gold text-black'
                          : 'bg-white/10 text-text-secondary hover:bg-white/20'
                      }`}
                    >
                      {template.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div>
                <Label className="text-text-secondary mb-2 block">Subject *</Label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  className="bg-white text-black"
                  placeholder="Email subject..."
                />
              </div>

              {/* Body */}
              <div>
                <Label className="text-text-secondary mb-2 block">Message *</Label>
                <textarea
                  value={emailForm.body}
                  onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                  className="w-full bg-white text-black rounded-lg p-4 min-h-[200px] resize-y border border-gray-300 focus:border-gold outline-none"
                  placeholder="Write your email message..."
                />
              </div>

              {/* Preview */}
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <div className="text-text-secondary text-sm mb-2">Preview:</div>
                <div className="text-sm whitespace-pre-wrap">{emailForm.body}</div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 pb-2">
                <Button
                  variant="outline"
                  onClick={() => { setShowEmailModal(false); setSelectedLead(null); }}
                  className="flex-1 border-white/20"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendEmail}
                  disabled={sendingEmail || !emailForm.subject || !emailForm.body}
                  className="flex-1 bg-gold text-black hover:bg-gold-light"
                >
                  {sendingEmail ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Email
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
      {timelineLeadId && (
        <LeadTimelineDrawer
          leadId={timelineLeadId}
          onClose={() => setTimelineLeadId(null)}
          canEdit={true}
        />
      )}
    </div>
  );
};

export default PartnerCRM;
